import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Toolbar, Typography, Box, Button, Table, TableBody, TableCell, TableContainer, TableRow, Paper } from "@mui/material";
import { setReconcileStatus, setStatementsState, updateTimeframe } from "../../../redux/features/reconcileSlice";
import StatementTable from "../../../components/statementTable/StatementTable";
import CustomSnackbar from "../../../components/common/Snackbar/CustomSnackbar";
import BalanceTable from "../../../components/common/BalanceTable";
import { getTransactionsFromData } from "../../../utils/utils";
import { getTransactions } from "../../../services/transactionService";
import { createReconcile, createUnReconcile } from "../../../services/reconcileService";
import { getOutOfBalance, getCashAccountStatement } from "../../../services/reportsService";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";


const ReconcileBankStatement = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Outstanding");
  const [loading, setLoading] = useState(false);
  const { statements, startFilterDate, endFilterDate } = useSelector((state: any) => state.reconcileState);
  const [onSuccess, setOnSuccess] = useState(false);
  const [investmentBalance, setInvestmentBalance] = useState(0);
  const [loanBalance, setLoanBalance] = useState(0);
  const [cashBalance, setCashBalance] = useState(0);
  const [outOfBalance, setOutOfBalance] = useState(0);

  const [beginningBalance, setBeginningBalance] = useState(0);
  const [additionsBalance, setAdditionsBalance] = useState(0);
  const [subtractionsBalance, setSubtractionsBalance] = useState(0);
  const [endingBalance, setEndingBalance] = useState(0);

  const [beginningDate, setBeginningDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endingDate, setEndingDate] = useState<Dayjs | null>(dayjs().endOf('month'));

  const [snackbarMsg] = useState("");
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const dispatch = useDispatch();
  const transferTransactionCode = "Transfer";


  const getAllTransactions = () => {
    setLoading(true);
    getTransactions(startFilterDate, endFilterDate, transferTransactionCode)
      .then((res) => {
        const transactions = getTransactionsFromData(res);
        dispatch(setStatementsState(transactions));
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const onReconcile = () => {

    setLoading(true);
    // @ts-ignore
    rowSelectionModel.forEach((id: number) => {
      createReconcile(id).then(() => {
        dispatch(setReconcileStatus({ id, status: "Reconciled" }));
      }).catch((err) => {
        console.log(`Error: ${id}`, err);
      });
    });
    setLoading(false);
  };

  const onOutstanding = () => {
    // @ts-ignore
    rowSelectionModel.forEach((id: number) => {
      createUnReconcile(id).then(() => {
        dispatch(setReconcileStatus({ id, status: "Outstanding" }));
      }).catch((err) => {
        console.log(`Error: ${id}`, err);
      });
    });
  };

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearch(event.target.value);
  };

  const clearSearch = () => {
    setSearch("");
  };

  const filteredStatements = statements.filter((statement: any) => {
    return (
      (status === "All" || statement.status === status) && (!search || statement.amount === Number(search))
    );
  });

  useEffect(() => {
    dispatch(updateTimeframe("0"));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(updateTimeframe("0"));
    };
  }, []);

  useEffect(() => {
    if (startFilterDate && endFilterDate) {
      getAllTransactions();
    }
  }, [startFilterDate, endFilterDate]);

  useEffect(() => {
    getOutOfBalance()
      .then((res) => {
        setInvestmentBalance(res?.investmentBalance);
        setLoanBalance(res?.loanBalance);
        setCashBalance(res?.cashBalance);
        setOutOfBalance(res?.outOfBalance);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    getCashAccountStatement(beginningDate?.format("L"), endingDate?.format("L"))
      .then((res) => {
        setBeginningBalance(res?.beginningBalance);
        setAdditionsBalance(res?.additionsBalance);
        setSubtractionsBalance(res?.subtractionsBalance);
        setEndingBalance(res?.endingBalance);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [beginningDate, endingDate]);  

  const formatCurrency = (value: number) => {
    if (value !== null && value !== undefined) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    }
  };

  return (
    <>
      <Toolbar
        sx={{
          justifyContent: "space-between",
          margin: "0px 15px",
        }}
      >
        <Typography className="transactions-history-title">
          Reconcile Bank Statement
        </Typography>
        {status === "Outstanding" && rowSelectionModel.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            className="new-btn"
            component="button"
            onClick={onReconcile}
            disabled={loading}
          >
            RECONCILE ({rowSelectionModel.length})
          </Button>
        )}
        {status === "Reconciled" && rowSelectionModel.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            className="new-btn"
            component="button"
            onClick={onOutstanding}
            disabled={loading}
          >
            Mark as Outstanding ({rowSelectionModel.length})
          </Button>
        )}

      </Toolbar>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BalanceTable
          investmentBalance={investmentBalance}
          loanBalance={loanBalance}
          cashBalance={cashBalance}
          outOfBalance={outOfBalance}
        />
        <TableContainer component={Paper}
          elevation={0}
          sx={{
            display: "inline-block",
            margin: "auto",
            padding: 1,
            width: "fit-content"
          }}
        >
          <Table
            size="small"
            sx={{
              border: "none",
              width: "auto",
              tableLayout: "auto",
              maxWidth: "100%",
            }}
          >
            <TableBody>
              <TableRow key="date-picker-row">
                <TableCell component="th" scope="row" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  <Box className="date-picker-container">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Beginning"
                        className="date-picker-container"
                        slotProps={{ textField: { size: "small" } }}
                        value={beginningDate}
                        onChange={(newValue) => setBeginningDate(newValue)}
                        sx={{
                          "& .Mui-selected": {
                            color: "#FFFFFF",
                            borderRadius: 2,
                            borderWidth: 1,
                            borderColor: "#e91e63",
                            border: "1px solid",
                            backgroundColor: "#f8bbd0",
                          },
                        }}
                      />
                    </LocalizationProvider>              
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  <Box className="date-picker-container">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Ending"
                        className="date-picker-container"
                        slotProps={{ textField: { size: "small" } }}
                        value={endingDate}
                        onChange={(newValue) => setEndingDate(newValue)}
                        sx={{
                          "& .Mui-selected": {
                            color: "#FFFFFF",
                            borderRadius: 2,
                            borderWidth: 1,
                            borderColor: "#e91e63",
                            border: "1px solid",
                            backgroundColor: "#f8bbd0",
                          },
                        }}
                      />
                    </LocalizationProvider>              
                  </Box>
                </TableCell>
              </TableRow>

              <TableRow key="beginning-balance-row">
                <TableCell component="th" scope="row" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  Beginning Balance {beginningDate?.format('L')}:
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  {formatCurrency(beginningBalance)}
                </TableCell>
              </TableRow>

              <TableRow key="additions-row">
                <TableCell component="th" scope="row" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  Additions:
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  {formatCurrency(additionsBalance)}
                </TableCell>
              </TableRow>

              <TableRow key="subtractions-row">
                <TableCell component="th" scope="row" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  Subtractions:
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  {formatCurrency(subtractionsBalance)}
                </TableCell>
              </TableRow>

              <TableRow key="ending-balance-row">
                <TableCell component="th" scope="row" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  Ending Balance {endingDate?.format('L')}:
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: "none", whiteSpace: "nowrap" }}>
                  {formatCurrency(endingBalance)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ margin: "20px 40px" }}>
        <StatementTable
          data={filteredStatements}
          search={search}
          onSearchChange={handleSearchChange}
          setStatus={setStatus}
          status={status}
          clearSearch={clearSearch}
          // @ts-ignore
          rowSelectionModel={rowSelectionModel}
          setRowSelectionModel={setRowSelectionModel}
        />

        <CustomSnackbar
          open={onSuccess}
          message={snackbarMsg}
          setOpen={setOnSuccess}
        />
      </Box>
    </>
  );
};

export default ReconcileBankStatement;
